// Basic
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Select from '@/common-utils/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import splitLines from '@/app/modules/sims/funcs/splitLines';
import isValidSimNumber from '@/app/modules/sims/funcs/validateSimNumber';
import { getRepeatedValues } from '@/common-utils/functions/repeatedValues';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';

/**
 *
 * @param root0
 * @param root0.simProvider
 * @param root0.setSimProvider
 * @param root0.simNumberList
 * @param root0.setSimNumberList
 * @param root0.setDisableSubmit
 * @param root0.providersOptions
 * @returns AddSimCardsByProvider
 */
function AddSimCardsByProvider({ simProvider, setSimProvider, simNumberList, setSimNumberList, setDisableSubmit, providersOptions = null }) {

    // Zod Schema
    const formSchema = z.object({
        provider: z.string(),
        sims: z.number()
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { provider: simProvider, sims: '' }
    });

    // UseEffect
    useEffect(() => {
        if (simNumberList.length === 0
            || (getRepeatedValues(splitLines(simNumberList)).length > 0
                || splitLines(simNumberList).filter(element => !isValidSimNumber(element)).length > 0)
        ) {
            setDisableSubmit(true);
        } else {
            setDisableSubmit(false);
        }
    }, [simNumberList]);

    return (
        <div>
            <Form {...form}>
                <form>
                    {providersOptions &&
                        <FormField
                            name={'provider'}
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>
                                        <Entity namespace={nsSimCard} entity='modal.add.labels.provider' />
                                    </FormLabel>
                                    <FormControl>
                                        <Select
                                            {...field}
                                            options={providersOptions}
                                            namespace={nsSimCard}
                                            value={simProvider}
                                            onChange={(value) => {
                                                setSimProvider(value);
                                            }}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    }
                    <FormField
                        name={'sims'}
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='select-profile'>
                                <FormLabel required>
                                    <Entity namespace={nsSimCard} entity='modal.add.labels.sims' />
                                </FormLabel>
                                <FormControl>
                                    <>
                                        <Input
                                            {...field}
                                            type='textarea'
                                            autoComplete='off'
                                            value={simNumberList}
                                            onChange={(e) => {
                                                setSimNumberList(e.target.value);
                                            }}
                                            namespace={nsSimCard}
                                            entity={'modal.add.placeholder.sims'}
                                            className='h-8'
                                        />
                                        <div className='sim-total'>
                                            <Entity namespace={nsSimCard} entity='modal.add.text.number' data={{ simNumber: splitLines(simNumberList).length }} />
                                        </div>
                                        <div className='text-red-500'>
                                            <ul>
                                                {
                                                    getRepeatedValues(splitLines(simNumberList)).map((simAlreadyAdded, index) =>
                                                        <li key={`${simAlreadyAdded}-${index}`}>
                                                            <Entity entity='modal.add.text.exists' namespace={nsSimCard} data={{ simAlreadyAdded }} />
                                                        </li>
                                                    )
                                                }
                                            </ul>
                                            <ul>
                                                {
                                                    splitLines(simNumberList).filter(element => !isValidSimNumber(element)).map((invalidSimNumber, index) =>
                                                        <li key={`${invalidSimNumber}-${index}`}>
                                                            <Entity entity='modal.add.text.invalid' namespace={nsSimCard} data={{ invalidSimNumber }} />
                                                        </li>
                                                    )
                                                }
                                            </ul>
                                        </div>
                                    </>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </form>
            </Form>
        </div>
    );
}

AddSimCardsByProvider.propTypes = {
    simProvider: PropTypes.string,
    setSimProvider: PropTypes.func,
    simNumberList: PropTypes.string,
    setSimNumberList: PropTypes.func,
    setDisableSubmit: PropTypes.func,
    providersOptions: PropTypes.array
};

export default AddSimCardsByProvider;